import { useEffect } from 'react';

/**
 * SEO helper — imperatively updates <head> tags on route change so we don't
 * end up with duplicates alongside the fallback tags already present in
 * /app/frontend/public/index.html (needed for non-JS crawlers).
 *
 * Props
 *  - title:       page-specific title (site name is auto-appended)
 *  - description: 155-160 char meta description
 *  - path:        pathname (e.g. "/clients") used for canonical + og:url
 *  - image:       absolute URL for og/twitter image (optional)
 *  - type:        "website" | "article" | "profile" (default "website")
 *  - noindex:     boolean — sets robots meta to noindex, nofollow
 */

const SITE_NAME = 'David Ball Management';
const SITE_ORIGIN = 'https://dbmtalent.com';
const DEFAULT_IMAGE =
  'https://static.wixstatic.com/media/815797_c5cea849b6564df1abeba5e17b9347a3~mv2.jpeg/v1/fill/w_1200,h_630,fp_0.50_0.28,q_85,enc_avif,quality_auto/815797_c5cea849b6564df1abeba5e17b9347a3~mv2.jpeg';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, , key, val] = selector.match(/^meta\[(name|property)=["']?([^"'\]]+)["']?\]$/) || [];
    if (key && val) {
      el.setAttribute(key, val);
    } else {
      // fallback: parse "meta[name=foo]" or "meta[property=foo]"
      const m = selector.match(/^meta\[(name|property)=([^\]]+)\]$/);
      if (m) el.setAttribute(m[1], m[2].replace(/["']/g, ''));
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export default function Seo({
  title,
  description,
  path = '',
  image,
  type = 'website',
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — London Actors' & Writers' Agency`;
  const url = `${SITE_ORIGIN}${path || '/'}`;
  const ogImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('meta[name="description"]', 'content', description);
    setLink('canonical', url);

    // Robots
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else if (robots) {
      robots.remove();
    }

    // Open Graph
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
    setMeta('meta[property="og:locale"]', 'content', 'en_GB');
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', ogImage);

    // Twitter
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);
  }, [fullTitle, description, url, ogImage, type, noindex]);

  return null;
}
